/**
 * RichContentElement — F12.H T12.H.5.
 *
 * Treść sformatowana HTML (sanityzowana przez DOMPurify — tylko safe tagi).
 * Pozwala wstawić bogatszy opis w formularzu (linki, pogrubienia, listy).
 *
 * SECURITY: HTML jest sanityzowany DOMPurify z jawną whitelistą tagów
 * (h1-h6, p, ul, ol, li, strong, em, a, br, hr, span, div) i atrybutów
 * (href, title, target, rel). DOMPurify usuwa wszystkie atrybuty zdarzeń
 * (`on*`) i niebezpieczne schematy URI (`javascript:`, `data:`) niezależnie
 * od whitelisty — w przeciwieństwie do samoróbki regexowej, której obejścia
 * (`<a/onmouseover=…>`) prowadziły do stored XSS.
 *
 * Settings shape:
 *   { html: string }
 */

import DOMPurify from 'dompurify';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

/**
 * Whitelista tagów formatujących. DOMPurify odrzuca wszystko spoza listy
 * (w tym `<script>`/`<style>`/`<iframe>`) oraz — niezależnie od listy —
 * wszystkie atrybuty zdarzeń (`on*`) i niebezpieczne schematy URI
 * (`javascript:`, `data:` na linkach). Nie da się tego obejść jak regexa.
 */
const ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'a',
  'br',
  'hr',
  'span',
  'div',
];

/** Atrybuty dozwolone tylko na tagach z whitelisty. Świadomie BEZ `style` i `on*`. */
const ALLOWED_ATTR = ['href', 'title', 'target', 'rel'];

/** Sanitizer HTML oparty o DOMPurify (branżowy standard) — patrz komentarz przy `ALLOWED_TAGS`. */
function sanitizeHtml(html: string): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

export const RichContentElement: LayoutElementDefinition = {
  type: 'rich-content',
  category: 'tresc',
  isContainer: false,
  label: 'Treść sformatowana',
  icon: 'FileText',
  defaultSettings: () => ({
    html: '<p>Treść sformatowana — możesz użyć <strong>HTML</strong>, <em>linków</em>, list itp.</p>',
  }),
  propertiesEditor: ({ settings, onChange }) => {
    const html = String(settings.html ?? '');
    return (
      <div className="space-y-3" data-testid="rich-content-properties-editor">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            HTML (dozwolone tagi: h1–h6, p, ul/ol/li, strong, em, a, br, hr, span, div)
          </label>
          <textarea
            value={html}
            onChange={(e) => onChange({ html: e.target.value })}
            rows={8}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 font-mono text-xs"
            data-testid="rich-content-prop-html"
          />
          <p className="mt-1 text-xs text-text-tertiary">
            Tagi spoza listy + event handlery są usuwane przy renderowaniu.
          </p>
        </div>
      </div>
    );
  },
  render: ({ element, mode }) => {
    const settings = (element.settings ?? {}) as { html?: string };
    const html = settings.html ?? '';
    const sanitized = sanitizeHtml(html);
    if (!sanitized && mode === 'builder') {
      return (
        <div
          className="rounded border border-dashed border-border-secondary p-3 text-xs text-text-tertiary"
          data-testid={`rich-content-element-${element.id}`}
        >
          (pusta treść sformatowana — ustaw w panelu właściwości)
        </div>
      );
    }
    return (
      <div
        className="prose prose-sm max-w-none text-text-secondary [&_a]:text-accent-600 [&_a]:underline [&_h1]:text-text-primary [&_h2]:text-text-primary [&_h3]:text-text-primary"
        data-testid={`rich-content-element-${element.id}`}
        data-element-type="rich-content"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  },
};

// Eksport sanitizera dla testów
export { sanitizeHtml as _sanitizeHtmlForTest };
